/* eslint-disable @typescript-eslint/no-unsafe-return */
import type { AxiosError } from "axios";
import { makeAutoObservable } from "mobx";
import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";

import type { apiTypes} from "../../services";
import { getPublicIp } from "../../services";
import { api } from "../../services";
import type { SenderCode } from "../../services/APIService/types";
import { OtpValidationType} from "../../services/APIService/types";

import { AttributeShelf, LoaderShelf } from "../../shelves";
import ArrayAttributeShelf from "../../shelves/ArrayAttributeShelf";

import { onlyNumber } from "../../utils/formatters";
import CookieStorage from "../../utils/cookie";
import { env } from "../../config";
import { ACCESS_TK, LOCAL_STORAGE_DEVICE_ID_KEY, LOCAL_STORAGE_SESSION_ID } from "../../constants";
import type { TokenAccess } from "./types";
import type { CustomNavigateFunction } from "../../hooks";
import { EAuthType } from "../../enum/authentication";

export enum TokenStatusResponse {
	TOO_MANY_AUTH_ATTEMPS = "TOO_MANY_LOGIN_ATTEMPTS",
	LAST_MANY_LOGIN_ATTEMPTS = "LAST_MANY_LOGIN_ATTEMPTS",
	INVALID_CODE = "INVALID_CODE",
	EXPIRED_CODE = "EXPIRED_CODE",
	INCORRECT_CODE = "INCORRECT_CODE"
}

type LoginStep = "choosePhone" | "cpf" | "insertPhone" | "token";
type FeedbackSteps =
  "cpfError" | "creditDenialElegible" | "creditDenialInelegible" | "exceedAttempts" | "expiredDeal" | "isNotPhoneOwner" | "noFinance" | "notFound" | "numberNotValidated" | "openAccounts" | "openFinanceConnection" | "paymentDebit" | "unavailableCredit";

export type Steps = FeedbackSteps | LoginStep;
export interface OtpProps {
	cellphone: string | null;
	sender: SenderCode | null;
	smsCreated: Date | null;
	isWaitingList?: boolean;
}

interface CurrentUser {
	phoneNumber: string;
	documentNumber: string;
}

export class AuthenticationStore {
	public loader = new LoaderShelf();
	public tkLogin = new AttributeShelf<string | null>(null);
	public tokenValidationError =
		new AttributeShelf<apiTypes.TokenValidationError | null>(null);
	public userEligibility = new AttributeShelf<apiTypes.UserEligibility | null>(
		null,
	);
	public resSendOTP = new AttributeShelf<OtpProps | null> (null);

	public cpf = new AttributeShelf("");
	public selectedPhone = new AttributeShelf("");
	public sessionId = new AttributeShelf("");
	public steps = new ArrayAttributeShelf<Steps>(["cpf"]);
	public currentPhoneIndex = new AttributeShelf(0);
	public deviceId = new AttributeShelf<string | null>(null);

	public showBackButton = new AttributeShelf(true);
	public showNavBar = new AttributeShelf(true);
	public cpfValidationError = new AttributeShelf(false);
	public mustShowAlterLineModal = new AttributeShelf(false);
	public isOptIn = new AttributeShelf(false);
	public isMobile = new AttributeShelf(false);
	public isCheckNotification = new AttributeShelf(true);

	constructor() {
		makeAutoObservable(this);
	}

	// Verifica se está autenticado
	public get isAuthenticated(): boolean {
		return !!this.getToken();
	}

	// Extrai CPF e telefone de dentro do token
	public get currentUser(): CurrentUser | null {
		const token = this.getToken();
		if (!token) {
			return null;
		}

		const tokenPayload = JSON.parse(atob(token.split(".")[1])) as TokenAccess;
		const phoneNumber = tokenPayload.phone;

		const userData =  {
			documentNumber: tokenPayload.nrdocumento,
			phoneNumber,
		};

		return userData;
	}

	// Busca o DeviceID
	public get currentDeviceID(): string {
		const deviceId = this.deviceId.value;

		if (deviceId) {
			return deviceId;
		}

		const localStorageDeviceID = localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY);

		if (localStorageDeviceID) {
			this.deviceId.set(localStorageDeviceID);
			return localStorageDeviceID;
		} else {
			const newDeviceID = uuidv4();
			localStorage.setItem(LOCAL_STORAGE_DEVICE_ID_KEY, newDeviceID);
			return newDeviceID;
		}
	}

	public get getIsCheckNotification(): boolean {
		return this.isCheckNotification.value;
	}

	public set setIsCheckNotification(value: boolean) {
		this.isCheckNotification.set(value);
	}

	// Apaga todos dados de autenticação
	public logout = (): void => {
		this.tkLogin.set(null);
		this.sessionId.set("");
		localStorage.clear();
		sessionStorage.clear();
		CookieStorage.deleteCookie(ACCESS_TK);
	};

	public onBackPress = (navigate: CustomNavigateFunction): void => {
		switch (this.steps.getLastOne) {
			case "choosePhone":
			case "token":
				return this.steps.removeLastOne();
			case "cpf":
				if (window.history.length > 1) {
					return navigate(-1);
				}
				return window.close();
			default:
				// Remover o cookie que veio do Vivo Pay
				return navigate("/", { replace: true });
		}
	};

	// Busca no token nos cookies e retorna descriptografado
	public getToken(): string | null {
		const accessToken = this.tkLogin.value;

		if (accessToken) {
			return accessToken;
		}

		const encryptedToken = CookieStorage.getCookie(ACCESS_TK);

		if (encryptedToken) {
			const bytes = CryptoJS.AES.decrypt(encryptedToken, env.cryptoKey);
			const decryptedKey: string = bytes.toString(CryptoJS.enc.Utf8);

			if (decryptedKey) {
				this.onSaveTokenCookie(decryptedKey);
				return decryptedKey;
			}
		}

		return null;
	}

	// Inicia o processo de login enviando req para elegibilidade
	public fetchSendCPF = async (cpfMasked: string, isDeviceMobile: boolean, navigate: CustomNavigateFunction ) => {
		if (this.loader.isLoading) {
			return;
		}

		this.isMobile.set(isDeviceMobile);
		const cpf = onlyNumber(cpfMasked);
		this.cpf.set(cpf);

		try {
			await this.fetchEligibility(cpf, navigate);
			this.loader.end();
		} catch (error) {
			if (
				typeof error === "object" &&
				error !== null &&
				"response" in error &&
				typeof (error as AxiosError).response?.status === "number"
			) {
				const status = +(error as AxiosError).response!.status;

				if (status === 400) {
					return this.cpfValidationError.set(true);
				}

				if (status === 404) {
					return this.steps.addOnEnd("noFinance");
				}
			}
		} finally {
			this.loader.end();
		}
	};

	// Dá sequencia no login fazendo post /auth
	public sendSMSToken = async (
		phone: string,
		isInsertedPhone = false,
		navigate: CustomNavigateFunction,
	) => {
		if (this.loader.isLoading) {
			return;
		}

		try {
			this.selectedPhone.set(phone);
			this.loader.start();

			const { data } = await api.postAuth({
				authType: EAuthType.OTP,
				nrdocumento: this.cpf.value,
				phone: this.selectedPhone.value,
				delivery: {
					method: "SMS",
					contact: phone,
				},
				validationType: OtpValidationType.LOGIN_CROSS,
				userId: this.cpf.value,
			});
			this.resSendOTP.set({...data, cellphone: phone});
			this.steps.addOnEnd("token");
		} catch (e) {
			console.error(e);

			const error: AxiosError = e as AxiosError;

			if (!error.response) {
				return;
			}

			if (error.response.status === 400) {
				return this.steps.addOnStart("token");
				navigate("/"); // TODO: REMOVER
			}

			if (error.response.status === 403) {
				return this.steps.addOnEnd("exceedAttempts");
			}

			if (isInsertedPhone) {
				const responseData = error.response.data as { status?: string; reason?: string };
				if (
					responseData.status === "INVALID_NUMBER" &&
					responseData.reason === "INVALID_NUMBER"
				) {
					return this.steps.addOnEnd("isNotPhoneOwner");
				}
			}

			throw error;
		} finally {
			this.loader.end();
		}
	};

	// Valida o token OTP
	public signOtpValidation = async (token: string, cpf: string, navigate: CustomNavigateFunction) => {
		if (this.loader.isLoading) {
			return;
		}

		try {
			this.loader.start();

			let contact: string;
			const cellphone = this.resSendOTP.value?.cellphone;

			if (cellphone?.length === 11 && cellphone.includes("*")) {
				contact = cellphone;
			} else {
				contact = onlyNumber(cellphone!);
			}

			const publicIp = await getPublicIp();

			const response = await api.postAuthValidate(
				{
					publicIp,
					authType: EAuthType.OTP,
					validationType: OtpValidationType.LOGIN_CROSS,
					userId: cpf,
					token,
					nrdocumento: this.cpf.value,
					phone: contact,
				},
			);

			const bearerToken = typeof response.headers.token === "string" ? response.headers.token : String(response.headers.token ?? "");
			this.steps.setValue([]);
			this.onSaveTokenCookie(bearerToken);
			navigate("/authorization-inquiry-balance", { replace: true });
		} catch (e) {
			// TODO: tratar erros
			const error: AxiosError = e as AxiosError;
			const { status, attemptsLeft, reason } = (error.response?.data ?? {}) as {
				status?: number | string;
				attemptsLeft?: number;
				reason?: string;
			};

			if (reason === TokenStatusResponse.EXPIRED_CODE) {
				return this.tokenValidationError.set({
					attemptsLeft: +attemptsLeft!,
					erroType: "expiredToken",
				});
			}

			if (status === TokenStatusResponse.LAST_MANY_LOGIN_ATTEMPTS) {
				return this.tokenValidationError.set({
					attemptsLeft: +attemptsLeft!,
					erroType: "blockedToken",
				});
			}

			if (status === TokenStatusResponse.TOO_MANY_AUTH_ATTEMPS) {
				return this.steps.addOnEnd("exceedAttempts");
			}

			if (status === TokenStatusResponse.INVALID_CODE) {
				return this.tokenValidationError.set({
					attemptsLeft: +attemptsLeft!,
					erroType: "invalidToken",
				});
			}


			switch (error.response?.status) {
				case 400:

					break;
				case 401:
					navigate("/expired-token");
					break;
				case 403:
					navigate("/system-maintenance");
					break;
				case 404:
					navigate("/system-maintenance");
					break;
				case 500:
					navigate("/system-maintenance");
					break;

				default:
					navigate("/system-maintenance");
					break;
			}
		} finally {
			this.loader.end();
		}
	};

	// Selecione outro telefone
	public onChooseAnotherPhoneOnTokenStepsClick = () => {
		if (this.userEligibility.value) {
			const amountOfUserLine = this.userEligibility.value.phones.length;

			if (amountOfUserLine >= 1) {
				return this.steps.addOnEnd("choosePhone");
			}

			return this.steps.addOnEnd("insertPhone");
		}
	};

	// Verifica as linhas e redireciona para step correto
	private onSuccess = async (navigate: CustomNavigateFunction) => {
		const phones = this.userEligibility.value?.phones;

		if (!phones) {
			return this.steps.addOnEnd("insertPhone");
		}

		// checa a linha
		switch (phones.length) {
			case 1:
				await this.sendSMSToken(phones[0].number, false, navigate);
				break;
			default:
				this.steps.addOnEnd("choosePhone");
				break;
		}
	};

	// GET Elegibilidade and OPT-IN
	private fetchEligibility = async (cpf: string, navigate: CustomNavigateFunction) => {
		if (this.loader.isLoading) {
			return;
		}

		try {
			this.loader.start();

			const eligibility = await api.getUserEligibility(cpf);

			this.userEligibility.set(eligibility);

			if (eligibility.phones.length > 0) {
				this.loader.end();
				await this.onSuccess(navigate);
			}
		} catch (e) {
			window.console.error(e);
			throw e;

		} finally {
			this.loader.end();
		}
	};

	// salva o token no local nos cookies
	private onSaveTokenCookie = (tk: string) => {
		if (!tk) {
			return;
		}

		this.tkLogin.set(tk);

		const encrypted: string = CryptoJS.AES.encrypt(tk, env.cryptoKey).toString();
		CookieStorage.setCookie(ACCESS_TK, encrypted); // ← salva no cookie

		const hashID = uuidv4();
		this.sessionId.set(hashID);
		localStorage.setItem(LOCAL_STORAGE_SESSION_ID, hashID);

	};
}
